import {
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Group,
    List,
    Paper,
    Progress,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title
} from '@mantine/core';
import { IconBrandWhatsapp, IconCheck } from '@tabler/icons-react';
import { getPaymentLink } from '../../../../servicos/mercadopago';
import './SecaoAssinatura.css';

const BILLING_CYCLES = [
    {
        id: 'mensal',
        name: 'Mensal',
        price: 120,
        label: 'R$ 120,00',
        savings: null,
        period: 'mês',
        months: 1,
        link: 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=5a913a04301d44b4ad94414c3746a106'
    },
    {
        id: 'trimestral',
        name: 'Trimestral',
        price: 340,
        label: 'R$ 340,00',
        savings: 'Economize 5%',
        period: '3 meses',
        months: 3,
        link: 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=fbab89812dac493597ab0aeedbcdcc2b'
    },
    {
        id: 'semestral',
        name: 'Semestral',
        price: 640,
        label: 'R$ 640,00',
        savings: 'Economize 11%',
        period: '6 meses',
        months: 6,
        link: 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=ad8d7dcaa1704f2aaab0fab5be6f2737'
    },
    {
        id: 'anual',
        name: 'Anual',
        price: 1150,
        label: 'R$ 1.150,00',
        savings: 'Economize 20%',
        period: 'ano',
        months: 12,
        link: 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=af543a8bfc4a42b4a7273930ba79371a'
    },
];

const SecaoAssinatura = ({ config, barbers = [], currentTenant }) => {
    const currentPlan = { name: 'Plano Profissional', price: 'R$ 120,00', limit: 20 };
    const usagePercent = Math.min((barbers.length / currentPlan.limit) * 100, 100);

    const getDaysRemaining = () => {
        if (!config?.dueDate) return null;
        try {
            const due = config.dueDate?.seconds ? new Date(config.dueDate.seconds * 1000) : new Date(config.dueDate);
            const now = new Date();
            const diffTime = due - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        } catch (e) {
            return null;
        }
    };

    const getRenewalDate = () => {
        try {
            if (config?.dueDate) {
                const date = config.dueDate?.seconds ? new Date(config.dueDate.seconds * 1000) : new Date(config.dueDate);
                return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
            }
            if (!config?.createdAt) return 'Próximo mês';
            const date = config.createdAt?.seconds ? new Date(config.createdAt.seconds * 1000) : new Date(config.createdAt);
            if (isNaN(date.getTime())) return 'Próximo mês';
            date.setMonth(date.getMonth() + 1);
            return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(date);
        } catch (error) {
            return 'Próximo mês';
        }
    };

    const daysRemaining = getDaysRemaining();

    return (
        <Stack spacing={{ base: 'md', sm: 'xl' }} p={{ base: 'xs', sm: 'md' }} className="secao-assinatura-container">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: 'md', sm: 'xl' }}>
                {/* Current Plan Card */}
                <Card withBorder radius="lg" p={{ base: 'lg', sm: 'xl' }} className={config?.trialMode ? "plan-card-trial" : "plan-card-premium"}>
                    <Stack spacing="sm">
                        <Group justify="space-between">
                            {config?.trialMode ? (
                                <Badge color="orange" variant="filled" size="lg">Período de Teste</Badge>
                            ) : (
                                <Badge variant="filled" size="lg">Plano Ativo</Badge>
                            )}
                            <Title order={3} color="white">{currentPlan.name}</Title>
                        </Group>

                        {config?.trialMode ? (
                            <Group spacing="xs">
                                <Box w={10} h={10} bg="orange" style={{ borderRadius: '50%' }} />
                                <Text size="sm" fw={700} color="orange">
                                    {daysRemaining > 0 ? `Restam ${daysRemaining} dias de teste` : 'Teste expirado'}
                                </Text>
                            </Group>
                        ) : (
                            <Group spacing="xs">
                                <Box w={10} h={10} bg="green" style={{ borderRadius: '50%' }} />
                                <Text size="sm" fw={700} color="green">Assinatura Ativa</Text>
                            </Group>
                        )}

                        <Divider my="sm" />

                        <Group justify="space-between">
                            <Box>
                                <Text size="xs" color="dimmed" fw={700} tt="uppercase">
                                    {config?.trialMode ? 'Expira em' : 'Próxima Renovação'}
                                </Text>
                                <Text fw={800} color="white">{getRenewalDate()}</Text>
                            </Box>
                            <Box align="right">
                                <Text size="xs" color="dimmed" fw={700} tt="uppercase">Valor Regular</Text>
                                <Text fw={900} size="lg" color="#DAA520">{currentPlan.price}</Text>
                            </Box>
                        </Group>
                    </Stack>
                </Card>

                {/* Usage Card */}
                <Card withBorder radius="lg" p={{ base: 'lg', sm: 'xl' }} className="usage-card">
                    <Title order={4} mb="lg" className="section-title">Uso do Sistema</Title>
                    <Stack spacing="md">
                        <Box>
                            <Group justify="space-between" mb={5}>
                                <Text size="sm" fw={700} color="white">Profissionais Cadastrados</Text>
                                <Text size="sm" color="dimmed">{barbers.length} / {currentPlan.limit}</Text>
                            </Group>
                            <Progress value={usagePercent} size="lg" color={usagePercent > 90 ? 'red' : 'blue'} radius="xl" />
                        </Box>

                        <Box>
                            <Group justify="space-between" mb={5}>
                                <Text size="sm" fw={700} color="white">Agendamentos Online</Text>
                                <Text size="sm" color="dimmed">Ilimitado</Text>
                            </Group>
                            <Progress value={100} size="lg" color="green" radius="xl" />
                        </Box>
                    </Stack>
                </Card>
            </SimpleGrid>

            {/* Plans Showcase */}
            <Box mt="xl">
                <Title order={3} mb="xl" ta="center" className="section-title">Escolha seu Ciclo de Assinatura</Title>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
                    {BILLING_CYCLES.map((cycle) => (
                        <Paper key={cycle.id} withBorder p="xl" radius="lg" className="plan-showcase-item" style={{ position: 'relative' }}>
                            {cycle.savings && (
                                <Badge color="green" variant="filled" style={{ position: 'absolute', top: -10, right: 10 }}>
                                    {cycle.savings}
                                </Badge>
                            )}
                            <Stack h="100%" justify="space-between">
                                <Box>
                                    <Text fw={900} size="lg" color="white">{cycle.name}</Text>
                                    <Group spacing={4} align="baseline">
                                        <Text fw={900} size="xl" color="#DAA520">{cycle.label}</Text>
                                        <Text size="xs" color="dimmed">/{cycle.period}</Text>
                                    </Group>
                                </Box>

                                <List
                                    spacing="xs"
                                    size="xs"
                                    center
                                    icon={<ThemeIcon color="green" size={16} radius="xl"><IconCheck size={10} /></ThemeIcon>}
                                    mt="md"
                                >
                                    <List.Item>Acesso Total ao Sistema</List.Item>
                                    <List.Item>Suporte Prioritário</List.Item>
                                    <List.Item>Agendamentos Ilimitados</List.Item>
                                </List>

                                <Button
                                    fullWidth
                                    mt="xl"
                                    className="btn-primary"
                                    onClick={() => {
                                        const dynamicLink = getPaymentLink('profissional', currentTenant, cycle.months, 'renewal');
                                        window.open(dynamicLink, '_blank');
                                    }}
                                >
                                    Assinar {cycle.name}
                                </Button>
                            </Stack>
                        </Paper>
                    ))}
                </SimpleGrid>
            </Box>

            {/* Renewal & Support Info */}
            <Paper withBorder p={{ base: 'lg', sm: 'xl' }} radius="lg" className="pix-info-card" mt="xl">
                <Title order={4} mb="md" color="white">Como renovar ou mudar de plano?</Title>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ base: 'lg', sm: 'xl' }}>
                    <Stack spacing="xs">
                        <Text size="sm" color="white">Para renovar sua assinatura, mudar de plano ou tirar dúvidas sobre pagamentos, entre em contato diretamente com nosso suporte via WhatsApp.</Text>
                        <Text size="sm" fw={700} color="#DAA520">A ativação é imediata após a confirmação do pagamento.</Text>
                    </Stack>
                    <Stack justify="center">
                        <Button
                            size="lg"
                            className="support-button-whatsapp"
                            leftSection={<IconBrandWhatsapp size={20} />}
                            onClick={() => window.open(`http://wa.me/5534997354131?text=Olá! Quero tratar sobre minha assinatura para a unidade ${currentTenant}.`, '_blank')}
                        >
                            <span className="desktop-text">Falar com Suporte (Assinatura)</span>
                            <span className="mobile-text">Suporte</span>
                        </Button>
                        <Text size="xs" color="dimmed" align="center">Atendimento prioritário para parceiros</Text>
                    </Stack>
                </SimpleGrid>
            </Paper>
        </Stack>
    );
};

export default SecaoAssinatura;
